import {
  useInitialDateNow,
  useIsEndTimeReached,
  useLocale,
} from '@aboutyou/i18n';
import {Log} from '@aboutyou/mlm';
import {useMergedFeatureFlagConfig} from './Provider';
import {useConfigWithExperimentData} from './abtests/useConfigWithExperimentData';
import {
  featureDisabledByLocales,
  featureEnabledByLocalesNoTimeFrameNorExperiment,
  getExperimentValue,
} from './helpers';
import {AvailableFeatureNames, FeatureToggleConfig} from './types';

/**
 * @deprecated use useIsFeatureActive instead
 *
 * Can be used to change a code path based on a global configuration or active ab test cookies.
 * The soft feature toggle can change at runtime, as it might be relative to a time specific locale.
 *
 * Compared to a hard feature flag, this will not allow to code split and optimize the code.
 *
 * @param featureName The name of the feature flag. This name needs to be present in the config,
 *                    which by default is defined in `packages/libraries/feature-flags/defaults.ts`.
 * @param config      Allows to pass defaults for feature flag. This defaults to the default config maintained in the feature flag repository.
 *                    This property is not intended to be used in any production code,
 *                    it was mainly implemented for easier testing.
 *
 * @returns returns   a boolean indicating either the feature is active or not
 *                    or a string that matches possible feature state values of experiment running.
 *                    Return value is based on Time frame, Locale, Experiment State.
 */
export const useIsSoftFeatureToggleActive = (
  featureName: AvailableFeatureNames,
  expensiveDateNow?: number,
): boolean | string => {
  const mergedFeatureFlagConfig = useMergedFeatureFlagConfig();
  const currentConfig = mergedFeatureFlagConfig[featureName];

  const locale = useLocale();
  const initialDateNow = useInitialDateNow();
  const dateNow = expensiveDateNow || initialDateNow;
  const combinedConfig = useConfigWithExperimentData(featureName);

  const anyDateBeforeToday = new Date(0);
  const isEndTimeReached = useIsEndTimeReached(
    new Date(combinedConfig.timeFrame?.end?.time || anyDateBeforeToday),
    60000,
  );

  if (currentConfig === undefined) {
    Log.warn(
      '@aboutyou/feature-flags',
      `Tried to access an unknown feature flag. To remove this warning either go to \`packages/libraries/feature-flags/defaults.ts\` and add it or remove this check from the code.`,
      {
        featureName,
      },
    );
    return false;
  }

  if (
    combinedConfig.enableForLocales !== undefined &&
    !Array.isArray(combinedConfig.enableForLocales)
  ) {
    Log.warn(
      '@aboutyou/feature-flags',
      `Tried to check locales from feature flag but received a wrong value. locales always needs to be a string of locales.`,
      {
        featureName,
        enableForLocales: combinedConfig.enableForLocales,
        currentConfig,
      },
    );
  }

  const isFeatureDisabledByLocales = featureDisabledByLocales({
    combinedConfig,
    locale,
  });

  if (isFeatureDisabledByLocales !== undefined) {
    return isFeatureDisabledByLocales;
  }

  const isFeatureEnabledByLocalesNoTimeFrameNorExperiment =
    featureEnabledByLocalesNoTimeFrameNorExperiment({combinedConfig, locale});

  if (isFeatureEnabledByLocalesNoTimeFrameNorExperiment !== undefined) {
    if (typeof combinedConfig.defaultValue !== 'boolean') {
      return combinedConfig.defaultValue;
    }
    return isFeatureEnabledByLocalesNoTimeFrameNorExperiment;
  }

  const isEndDateHasBeenReached = endDateHasBeenReached({
    combinedConfig,
    isEndTimeReached,
  });

  if (isEndDateHasBeenReached !== undefined) {
    return isEndDateHasBeenReached;
  }

  if ('experiment' in combinedConfig && !!combinedConfig.experiment) {
    return getExperimentValue(combinedConfig.experiment);
  }
  const isStartTimeHasBeenReached = startTimeHasBeenReached({
    combinedConfig,
    isEndTimeReached,
    featureName,
    dateNow,
  });

  if (isStartTimeHasBeenReached !== undefined) {
    return isStartTimeHasBeenReached;
  }

  return combinedConfig.defaultValue ?? false;
};

const endDateHasBeenReached = ({
  combinedConfig,
  isEndTimeReached,
}: {
  combinedConfig: FeatureToggleConfig;
  isEndTimeReached: boolean;
}) => {
  if (!combinedConfig.timeFrame) {
    return undefined;
  }

  if (combinedConfig.timeFrame.end && isEndTimeReached) {
    return combinedConfig.timeFrame.end.value;
  }

  return undefined;
};

const startTimeHasBeenReached = ({
  combinedConfig,
  isEndTimeReached,
  featureName,
  dateNow,
}: {
  combinedConfig: FeatureToggleConfig;
  isEndTimeReached: boolean;
  featureName: AvailableFeatureNames;
  dateNow: number;
}) => {
  if (!combinedConfig.timeFrame) {
    return undefined;
  }

  const startTime = new Date(combinedConfig.timeFrame.start.time).getTime();

  if (isNaN(startTime)) {
    Log.warn(
      '@aboutyou/feature-flags',
      `Tried to parse start time. Failed because of invalid date format.`,
      {
        featureName,
        enableForLocales: combinedConfig.enableForLocales,
      },
    );

    return undefined;
  }

  if (combinedConfig.timeFrame.end?.time && isEndTimeReached) {
    return undefined;
  }

  if (dateNow - startTime < 0) {
    return undefined;
  }

  return combinedConfig.timeFrame.start.value;
};
